import { useEffect, useState } from 'react';
import { Check, CopyIcon, Share } from 'lucide-react';
import { getReferralStats, sendReferralCode } from '../../auth/_requests';
import { showToast } from '../../helpers/utils/showToast';
import { useModal } from '../../helpers/hooks/modalContext';
import { useAuth } from '../../auth/Auth';
import { ReferralStats } from '../../helpers/utils/types';
import { isValidEmail } from '../../helpers/utils/utils';
import { InlineAlert } from '../../helpers/UI/InlineAlert/InlineAlert';

interface Props {
  code: string;
  copied: boolean;
  handleCopy: () => void;
  setCopied: (val: boolean) => void;
}

const ReferralActions: React.FC<Props> = ({ code, copied, handleCopy }) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const { auth } = useAuth();
  const { setModal } = useModal();
  const [referralStats, setReferralStats] = useState<ReferralStats>();
  const [serverError, setServerError] = useState<string | null>(null);

  const sendCodeOnEmail = async () => {
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      const response = await sendReferralCode(auth?.data.token, email);
      if (response.message === 'success') {
        showToast({ message: 'The invitation has been sent successfully', type: 'success' });
        setEmail('');
        setShowEmailModal(false);
        fetchReferralStats();
      } else if (['already_invited', 'already_registered'].includes(response.message)) {
        setServerError('This email has already been invited or registered');
      }
    } catch {
      console.error('Error sending code on email');
    } finally {
      setLoading(false);
    }
  };
  const fetchReferralStats = async () => {
    try {
      const response = await getReferralStats(auth?.data.token);
      if (response) {
        setReferralStats(response);
      }
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    }
  };
  useEffect(() => {
    fetchReferralStats();
  }, [auth?.data.token]);

  return (
    <div className='flex flex-col items-start gap-10 w-full'>
      <div className='relative w-full flex items-center'>
        <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] z-0 pointer-events-none'></div>
        <div className='relative z-5 w-full rounded-[20px] border border-[#E9EDF9] bg-[linear-gradient(229deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-8 py-[32px]'>
          <div className='flex flex-col items-center gap-6 w-full '>
            <div className='flex flex-col items-start gap-4 w-full '>
              <span className='text-main text-base'>Share your referral link</span>
              <div className='inputField w-full justify-between flex'>
                <span className='truncate max-w-[calc(100%-40px)] overflow-hidden whitespace-nowrap'>
                  {code}
                </span>
                <div className='relative'>
                  <button onClick={handleCopy}>
                    {copied ? (
                      <Check stroke={'var(--primary-color'} className='h-5 w-5' />
                    ) : (
                      <CopyIcon stroke={'var(--primary-color'} className='h-5 w-5' />
                    )}
                  </button>
                </div>
              </div>
              <button
                className='submitBtn w-full'
                onClick={() => setShowEmailModal(true)}
                disabled={loading || !code}
              >
                <div className='flex justify-between w-full p-5'>
                  <span>Share</span>
                  <Share className='text-white h-5 w-5' />
                </div>
              </button>

              {showEmailModal && (
                <>
                  <div
                    className='inputWrapper w-full'
                    title="Enter recipient's email to share the referral code"
                  >
                    <div className='label'>Email</div>
                    {serverError && (
                      <InlineAlert
                        type='error'
                        message={serverError}
                        dismissible
                        onDismiss={() => setServerError('')}
                      />
                    )}
                    <div
                      className='inputField'
                      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                    >
                      <input
                        autoComplete='off'
                        type='email'
                        placeholder={`Enter recipient's email`}
                        value={email}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEmail(value);
                          if (!isValidEmail(value)) {
                            setEmailError('Please enter a valid email address');
                          } else {
                            setEmailError('');
                          }
                        }}
                        onBlur={() => {
                          if (email && !isValidEmail(email)) {
                            setEmailError('Please enter a valid email address');
                          }
                        }}
                      />
                    </div>
                    {emailError && <span className='text-red-500 text-sm mt-1'>{emailError}</span>}
                  </div>

                  <button
                    onClick={sendCodeOnEmail}
                    className={`submitBtn ${
                      loading || !email || !isValidEmail(email)
                        ? '!bg-[var(--disable-btn)] text-[var(--disable-text-color)] cursor-not-allowed'
                        : ''
                    }`}
                    disabled={loading || !email || !isValidEmail(email)}
                  >
                    Share
                  </button>
                  <button className='cancelBtn' onClick={() => setShowEmailModal(false)}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`relative w-full flex items-center ${
          (referralStats?.totalInvited ?? 0) + (referralStats?.totalUninvited ?? 0) > 0
            ? 'cursor-pointer hover:scale-105 transition-transform duration-200 ease-in-out'
            : 'cursor-default opacity-60'
        }`}
        onClick={() => {
          if ((referralStats?.totalInvited ?? 0) + (referralStats?.totalUninvited ?? 0) > 0) {
            setModal('referral-history');
          }
        }}
      >
        <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] z-0 pointer-events-none'></div>
        <div className='relative z-5 w-full rounded-[20px] border border-[#E9EDF9] bg-[linear-gradient(229deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-8 py-[32px]'>
          <div className='flex flex-col gap-6 w-full'>
            <div className='flex flex-col items-start gap-4'>
              <span className='text-main text-lg'>Referral history</span>
              <div className='flex gap-10'>
                <div className='flex flex-col items-start gap-2'>
                  <span className='text-light/70 text-base'>Total earned (Pts)</span>
                  <span className='text-primary text-2xl font-semibold'>
                    {Number(referralStats?.totalPoints ?? 0).toLocaleString('en-US')}
                  </span>
                </div>
                <div className='flex flex-col items-start gap-2'>
                  <span className='text-light/70 text-base'>Friends invited</span>
                  <span className='text-primary text-2xl font-semibold'>
                    {(referralStats?.totalInvited ?? 0) + (referralStats?.totalUninvited ?? 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralActions;
